import {Injectable} from '@angular/core';
import {StoreService} from './store.service';
import {EntityStoreState} from '../models/entity-store-state.model';
import {HashMap} from '../models/hash-map.model';
import {ActiveOptions} from '../types/active-options.type';
import {UpdateStateCallback} from '../types/update-state-callback.type';

@Injectable({
    providedIn: 'root',
})
export class EntityStoreService<EntityStateType extends EntityStoreState<EntityType>, EntityType> extends StoreService<EntityStateType> {
    public update(id: string | string[], newStateFn: UpdateStateCallback<EntityType>): void;
    public update(id: string | string[], newState: Partial<EntityType>): void;
    public update(newState: UpdateStateCallback<EntityStateType>): void;
    public update(newState: Partial<EntityStateType>): void;
    public update(
        idsOrStateOrFnc: string | string[] | null | Partial<EntityStateType> | UpdateStateCallback<EntityStateType>,
        newStateOrFn?: Partial<EntityType> | UpdateStateCallback<EntityType> | Partial<EntityStateType>
    ): void {
        if (newStateOrFn === undefined) {
            super.update(idsOrStateOrFnc as Partial<EntityStateType>);
            return;
        }

        const newState: EntityStateType = this.getValue();
        const ids: Array<string> = newState.ids;
        if (!Array.isArray(ids) || ids.length === 0) {
            return;
        }

        let selectedIds: Array<string>;
        if (!Array.isArray(idsOrStateOrFnc)) {
            selectedIds = [idsOrStateOrFnc as string];
        } else {
            selectedIds = idsOrStateOrFnc;
        }

        const entities: HashMap<EntityType> = newState.entities;
        for (const id of selectedIds) {
            entities[id] = { ...entities[id], ...newStateOrFn };
        }
        super.update(state => ({ ...state, ...newState }));
    }

    public setLoading(loading: boolean): void {
        this.update(state => ({ ...state, loading }));
    }

    public setActive(idsOrActiveOptions: Array<string>): void;
    public setActive(idsOrActiveOptions: ActiveOptions): void;
    public setActive(idsOrActiveOptions: Array<string> | ActiveOptions): void {
        if (Array.isArray(idsOrActiveOptions)) {
            this.update(state => ({ ...state, active: idsOrActiveOptions }));
        } else {
            const currentState: EntityStoreState<EntityType> = this.getValue();
            if (currentState.active[0]) {
                const ids = currentState.ids;
                const currentIdIndex = ids.indexOf(currentState.active[0]);
                const isLastId = ids.length === currentIdIndex + 1;
                const nextId = isLastId ? ids[0] : (ids[currentIdIndex + 1]);
                this.update(state => ({ ...state, active: [nextId] }));
            }
        }
    }

    public addActives(ids: Array<string>): void {
        if (ids.length === 0) {
            return;
        }
        const everyExist = ids.every(id => this.getValue()
            .active
            .includes(id));
        if (everyExist) {
            return;
        }

        this.update((state: EntityStateType) => {
            return {
                ...state,
                active: Array.from(new Set([...state.active, ...ids]))
            };
        });
    }

    public removeActive(ids: Array<string> | string): void {
        if (!Array.isArray(ids)) {
            ids = [ids];
        }
        const active = this.getValue()
            .active
            .filter(id => !ids.includes(id));
        this.update(state => {
            return { ...state, active };
        });
    }

    public upsert<NewEntityType extends Partial<EntityType>>(id: string, newEntityState: NewEntityType): void {
        const currentState: EntityStoreState<EntityType> = this.getValue();
        const ids: Array<string> = currentState.ids;
        const entities: HashMap<EntityType> = currentState.entities;
        if (!(id in entities)) {
            entities[id] = {} as EntityType;
        }
        entities[id] = { ...entities[id], ...{ id }, ...newEntityState };
        if (!ids.includes(id)) {
            ids.push(id);
        }
        this.update(state => ({
            ...state,
            ids,
            entities
        }));
    }

    public upsertMany<NewEntityType extends Partial<EntityType>>(newEntityStates: Array<NewEntityType>): void {
        for (const newEntityState of newEntityStates) {
            this.upsert((newEntityState as NewEntityType & { id: string }).id, newEntityState);
        }
    }

    public set<EntityType>(entities: Array<EntityType>): void;
    public set<EntityType>(entities: HashMap<EntityType>): void;
    public set<EntityType>(entities: Array<EntityType> | HashMap<EntityType>): void {
        let ids;
        let newEntities: HashMap<EntityType> = {};
        if (Array.isArray(entities)) {
            ids = (entities as Array<EntityType & { id: string }>).map(entity => entity.id);
            for (const entity of entities) {
                newEntities[(entity as EntityType & { id: string }).id] = entity;
            }
        } else {
            ids = Object.keys(entities);
            newEntities = entities;
        }

        this.update((state) => {
            const active = state.active.filter(id => ids.includes(id));
            return { ...state, ids, entities: newEntities, ...active, loading: false };
        });
    }

    public remove(ids: Array<string> | string): void {
        if (!Array.isArray(ids)) {
            ids = [ids];
        }
        const currentState: EntityStoreState<EntityType> = this.getValue();
        for (const id of ids) {
            delete currentState.entities[id];
        }
        this.set(currentState.entities);
    }
}
